import type { CSSProperties, MouseEvent, ReactNode } from "react";

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
} from "lucide-react";

import { cx, styled } from "simplydo/react/styled";

import { Icon } from "../icon/Icon";
import type { UISize } from "../utils/UIContext";
import { SIZE_FONT } from "../utils/colors";

export type PaginationProps = {
  activePage?: number | string;
  totalPages?: number | string;
  onPageChange?: (
    event: MouseEvent<HTMLElement>,
    data: { activePage: number },
  ) => void;
  size?: UISize;
  siblingRange?: number;
  boundaryRange?: number;
  firstItem?: ReactNode | null;
  lastItem?: ReactNode | null;
  prevItem?: ReactNode | null;
  nextItem?: ReactNode | null;
  ellipsisItem?: ReactNode | null;
  className?: string;
  style?: CSSProperties;
};

/**
 * Simplified vs Semantic Pagination:
 * - Renders first / prev / numbered window / next / last
 * - `siblingRange` / `boundaryRange` approximate Semantic windowing
 * - Item props accept `null` to hide, ReactNode to customise, or default icons
 */

const Nav = styled.div<{ $size?: UISize }>`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  font-size: ${({ $size }) => ($size && SIZE_FONT[$size]) || "1rem"};
  border: none;
  margin: 0;
  vertical-align: middle;
`;

const Item = styled.button<{ $active?: boolean; $disabled?: boolean }>`
  appearance: none;
  background: ${({ $active }) => ($active ? "#f2f2f2" : "#ffffff")};
  color: ${({ $active, $disabled }) =>
    $disabled
      ? "rgba(40, 40, 40, 0.3)"
      : $active
        ? "rgba(0, 0, 0, 0.95)"
        : "rgba(0, 0, 0, 0.87)"};
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-right-width: 0;
  min-width: 3em;
  min-height: 3em;
  padding: 0.78571429em 0.92857143em;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  font-weight: ${({ $active }) => ($active ? "bold" : "normal")};
  text-align: center;
  line-height: 1;
  font-family: inherit;
  font-size: inherit;

  &:first-child {
    border-radius: 0.28571429rem 0 0 0.28571429rem;
  }
  &:last-child {
    border-radius: 0 0.28571429rem 0.28571429rem 0;
    border-right-width: 1px;
  }
  &:hover:not(:disabled) {
    background: #fafafa;
  }
`;

function range(start: number, end: number): number[] {
  const out: number[] = [];
  for (let i = start; i <= end; i += 1) {
    out.push(i);
  }
  return out;
}

function buildPages(
  active: number,
  total: number,
  siblingRange: number,
  boundaryRange: number,
): (number | "ellipsis")[] {
  if (total <= 1) {
    return [1];
  }

  const startPages = range(1, Math.min(boundaryRange, total));
  const endPages = range(
    Math.max(total - boundaryRange + 1, boundaryRange + 1),
    total,
  );
  const siblingsStart = Math.max(
    Math.min(
      active - siblingRange,
      total - boundaryRange - siblingRange * 2 - 1,
    ),
    boundaryRange + 2,
  );
  const siblingsEnd = Math.min(
    Math.max(active + siblingRange, boundaryRange + siblingRange * 2 + 2),
    endPages.length > 0 ? (endPages[0] ?? total) - 2 : total - 1,
  );

  const pages: (number | "ellipsis")[] = [...startPages];

  if (siblingsStart > boundaryRange + 2) {
    pages.push("ellipsis");
  } else if (boundaryRange + 1 < total - boundaryRange) {
    pages.push(boundaryRange + 1);
  }

  pages.push(...range(siblingsStart, siblingsEnd));

  if (siblingsEnd < total - boundaryRange - 1) {
    pages.push("ellipsis");
  } else if (total - boundaryRange > boundaryRange) {
    pages.push(total - boundaryRange);
  }

  pages.push(...endPages);
  return [...new Set(pages)];
}
const PaginationComponent = (props: PaginationProps) => {
  const {
    activePage = 1,
    totalPages = 1,
    onPageChange,
    size,
    siblingRange = 1,
    boundaryRange = 1,
    firstItem,
    lastItem,
    prevItem,
    nextItem,
    ellipsisItem,
    className,
    style,
  } = props;
  const active = Number(activePage) || 1;
  const total = Math.max(1, Number(totalPages) || 1);
  const pages = buildPages(active, total, siblingRange, boundaryRange);

  const go = (e: MouseEvent<HTMLElement>, page: number) => {
    if (page < 1 || page > total || page === active) {
      return;
    }
    onPageChange?.(e, { activePage: page });
  };

  const renderNavItem = (
    key: string,
    page: number,
    node: ReactNode | null | undefined,
    fallback: ReactNode,
    disabled: boolean,
  ) => {
    if (node === null) {
      return null;
    }
    return (
      <Item
        key={key}
        type="button"
        $disabled={disabled}
        disabled={disabled}
        onClick={(e) => go(e, page)}
        aria-label={key}
      >
        {node === undefined ? fallback : node}
      </Item>
    );
  };

  return (
    <Nav className={cx(className)} style={style} $size={size} role="navigation">
      {renderNavItem(
        "first",
        1,
        firstItem,
        <Icon icon={ChevronsLeftIcon} />,
        active <= 1,
      )}
      {renderNavItem(
        "prev",
        active - 1,
        prevItem,
        <Icon icon={ChevronLeftIcon} />,
        active <= 1,
      )}
      {pages.map((p, i) => {
        if (p === "ellipsis") {
          if (ellipsisItem === null) {
            return null;
          }
          return (
            <Item key={`e-${i}`} type="button" disabled $disabled>
              {ellipsisItem === undefined ? "…" : ellipsisItem}
            </Item>
          );
        }
        return (
          <Item
            key={p}
            type="button"
            $active={p === active}
            aria-current={p === active ? "page" : undefined}
            onClick={(e) => go(e, p)}
          >
            {p}
          </Item>
        );
      })}
      {renderNavItem(
        "next",
        active + 1,
        nextItem,
        <Icon icon={ChevronRightIcon} />,
        active >= total,
      )}
      {renderNavItem(
        "last",
        total,
        lastItem,
        <Icon icon={ChevronsRightIcon} />,
        active >= total,
      )}
    </Nav>
  );
};

export const Pagination = styled(PaginationComponent)``;
